<script setup>
const props = defineProps(['nav']);
const gtm = useGtm();

const { locale, t } = useI18n();
const localePath = useLocalePath();

const store = useCommonStore();

const route = useRoute();
const isHomePage = computed(() => route.name && route.name.toString().startsWith('index'));

watchEffect(() => {
  document?.documentElement.style.setProperty('--footer-background', store.getPageColor);
});

const track = () => {
  gtm.trackEvent({ event: 'newsletter_visit', url: window.location.pathname });
};
</script>

<template>
  <div class="footer-container" :style="{ 'background-color': 'var(--footer-background)' }">
    <div class="footer visible animate__animated animate__fadeInUp">
      <section class="footer__connect">
        <div class="footer__connect-social">
          <div class="social-links">
            <a href="https://www.facebook.com/NGPrague" target="_blank" class="social-links__item">
              <i class="icon-facebook" />
              <span class="hidden">Facebook</span>
            </a>
            <a href="https://www.instagram.com/ngprague/" target="_blank" class="social-links__item">
              <i class="icon-instagram" />
              <span class="hidden">Instagram</span>
            </a>
            <a href="https://www.youtube.com/channel/UCeYULpYNcpUJxSeK2FpDpsg" target="_blank" class="social-links__item">
              <i class="icon-youtube" />
              <span class="hidden">YouTube</span>
            </a>
          </div>
        </div>
        <div class="footer__connect-newsletter">
          <NuxtLink :to="localePath('newsletter')" class="newsletter-link" @click.prevent="track()">{{ t('pages.index.subscribe-link') }}</NuxtLink>
        </div>
      </section>

      <CTAClub />
      <section class="footer__social mt-4">
        <FooterNav :items="nav" />
      </section>

      <section class="mt-5">
        <FooterPartners />
        <p class="footer__thank-you" v-html="t('pages.index.footer.thank-you')"></p>
        <NuxtLink :to="localePath('about-partners')" class="footer__partners-link link">{{ t('pages.index.link.show-all-partners') }}</NuxtLink>
      </section>

      <img src="/img/logo-230.svg" alt="NGP" class="footer__logo" loading="lazy" />

      <ul class="footer__links" :class="{ 'is-home': isHomePage }">
        <li>
          <NuxtLink
            :to="
              localePath({
                name: 'about-page-slug',
                params: {
                  slug: locale == 'cs' ? 'prohlaseni-o-pristupnosti' : 'accessibility-statement',
                },
              })
            "
            class="link"
          >
            {{ t('global.footer.accessibility') }}
          </NuxtLink>
        </li>
        <li v-if="locale !== 'en'">
          <NuxtLink
            :to="
              localePath({
                name: 'about-page-slug',
                params: {
                  slug: locale == 'cs' ? 'vyhrada-prav-k-vytezovani-textu-a-dat' : 'reservation-of-rights-for-text-and-data-mining',
                },
              })
            "
            class="link"
          >
            {{ t('global.footer.data_mining') }}
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            :to="
              localePath({
                name: 'about-page-slug',
                params: {
                  slug: 'gdpr',
                },
              })
            "
            class="link"
          >
            {{ t('global.footer.privacy') }}
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            :to="
              localePath({
                name: 'about-page-slug',
                params: {
                  slug: 'imprint',
                },
              })
            "
            class="link"
          >
            {{ t('global.footer.imprint') }}
          </NuxtLink>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.footer {
  margin: 0 $main-padding-mobile-x 0;
  padding-bottom: 30px;
  position: relative;
  transition: opacity 300ms 200ms;
  z-index: 2;

  border-top: 1px solid $black;

  .footer__connect {
    width: 60%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 22px 0;

    @media (max-width: 600px) {
      width: 100%;
    }
  }

  .footer__connect-newsletter {
    display: flex;
    align-items: center;
  }

  .footer__connect-social {
    display: flex;
    align-items: center;
  }

  &.is-not-visible {
    opacity: 0 !important;
    pointer-events: none;
    transition: opacity 300ms;
  }

  @include mq($lg) {
    border-bottom: 100px transparent solid;
    margin-left: 40px;
    margin-right: 40px;
  }

  @include mq($xl) {
    margin-left: 80px;
    margin-right: 80px;
  }

  &__logo {
    position: absolute;
    bottom: 30px;
    right: 0px;
    width: 80px;

    @include mq($lg) {
      bottom: 40px;
      right: 0px;
    }
  }

  &__social {
    padding: 20px 0;
    border-top: 1px solid $black;
    border-bottom: 1px solid $black;
  }

  &__thank-you {
    max-width: 340px;

    margin-top: 20px;
    margin-bottom: 0px;
    padding-right: 100px;
    font-size: 20px;
    line-height: 1.4;
    font-weight: $font-weight-medium;

    @include mq($md) {
      max-width: 100%;
    }

    @include mq($lg) {
      padding-right: 0;

      max-width: 240px;
      font-size: 16px;
    }

    @include mq($xxxl) {
      font-size: 20px;
      max-width: 100%;
    }
  }

  &__partners-link {
    display: inline-block;
    padding-top: 6px;
    font-size: 10px;
    line-height: 140%;

    @include mq($xxxl) {
      font-size: 14px;
    }
  }

  &__links {
    width: max-content;
    margin-top: 30px;

    li {
      display: block;
    }

    &:not(.is-home) li {
      @include mq($sm) {
        display: inline-block;

        &:not(:first-child) {
          margin-left: 10px;
        }
      }
    }

    a {
      font-size: 10px;
      line-height: 140%;

      @include mq($xxxl) {
        font-size: 14px;
      }
    }
  }
}

.main {
  &--narrow {
    + .footer {
      border-bottom-width: 30px;
      margin-bottom: 20px;
      margin-left: 20px;
      margin-right: 20px;

      @include mq($lg) {
        margin-bottom: 0;
        margin-left: 40px;
        margin-right: 40px;
      }
    }
  }
}

.social-links {
  $s: &;

  &__item {
    margin-right: 12px;
    font-size: 32px;

    &:last-child {
      margin-right: 0;
    }
  }

  &--small {
    #{$s}__elem {
      font-size: 28px;
    }
  }
}

.newsletter-link {
  font-size: 14px;
  font-weight: $font-weight-medium;
  text-transform: uppercase;

  @include underline-transition(1px);
}
</style>
